import * as React from 'react';
import {
    Stack,
    Typography,
    Autocomplete,
    TextField,
    SxProps,
    Theme
} from '@mui/material';


/**
 * Common filter types used across multiple chart components
 */

/**
 * Generic interface for any data that has Region, Country, and Company fields
 * for use with the PlantsFilter component
 */
export interface FilterableData {
    Region: string;
    Country: string;
    Company_Name: string;
    Plant_ID: string;
}

/**
 * Filter state for Region > Country > Company filtering
 * Now supports text input for country and company filtering
 */
export interface FilterData {
    region: string;        // Exact match selection (dropdown)
    country: string;       // Text input - filters countries containing this text (case-insensitive)
    company: string;       // Text input - filters companies containing this text (case-insensitive)
}

/**
 * Props for components that use Region > Country > Company filtering
 */
export interface FilterProps<T extends FilterableData> {
    /**
     * The raw data to filter
     */
    data: T[];

    /**
     * Current filter values
     */
    filters: FilterData;

    /**
     * Callback when filters change
     */
    onFiltersChange: (filters: FilterData) => void;

    /**
     * Callback that provides the filtered data
     */
    onFilteredDataChange: (filteredData: T[]) => void;
}


export interface PlantsFilterProps<T extends FilterableData> {
    /**
     * The raw data to filter
     */
    data: T[];

    /**
     * Current filter values
     */
    filters: FilterData;

    /**
     * Callback when filters change
     */
    onFiltersChange: (filters: FilterData) => void;

    /**
     * Callback that provides the filtered data
     */
    onFilteredDataChange: (filteredData: T[]) => void;

    /**
     * Optional spacing between filter controls
     * @default 2
     */
    spacing?: number;

    /**
     * Optional minimum width for filter controls
     * @default { region: 200, country: 200, company: 250 }
     */
    minWidths?: {
        region?: number;
        country?: number;
        company?: number;
    };

    /**
     * Whether to show the filtered data count
     * @default true
     */
    showDataCount?: boolean;

    /**
     * Custom label for the data count
     * @default "companies"
     */
    dataCountLabel?: string;

    /**
     * Optional sx props for the Stack container
     */
    sx?: SxProps<Theme>;
}

/**
 * A reusable component that provides Region > Country > Company filtering
 * for any dataset that has these three fields.
 * Now supports autocomplete for Country and Company filters with both dropdown and text input.
 * 
 * @example
 * ```tsx
 * <PlantsFilter
 *   data={businessData}
 *   filters={filters}
 *   onFiltersChange={setFilters}
 *   onFilteredDataChange={setFilteredData}
 *   dataCountLabel="companies"
 * />
 * ```
 */


const PlantsFilter: React.FC<PlantsFilterProps<any>> = <T extends FilterableData>({
    data,
    filters,
    onFiltersChange,
    onFilteredDataChange,
    spacing = 2,
    minWidths = { region: 200, country: 200, company: 250 },
    sx,
}: PlantsFilterProps<T>): React.ReactElement => {

    // Extract unique values for region filter (no "All" option - empty means all)
    const uniqueRegions: string[] = React.useMemo((): string[] => {
        return Array.from(new Set(data.map((d: T): string => d.Region))).sort();
    }, [data]);

    // Get available countries based on region filter (for autocomplete options)
    const availableCountries: string[] = React.useMemo((): string[] => {
        const filteredData = !filters.region || filters.region.trim() === ''
            ? data
            : data.filter((d: T): boolean => d.Region === filters.region);
        return Array.from(new Set(filteredData.map((d: T): string => d.Country))).sort();
    }, [data, filters.region]);

    // Get available companies based on region and country filters (for autocomplete options)
    const availableCompanies: string[] = React.useMemo((): string[] => {
        let filteredData = data;
        if (filters.region && filters.region.trim() !== '') {
            filteredData = filteredData.filter((d: T): boolean => d.Region === filters.region);
        }
        // Apply country text filter if not empty
        if (filters.country && filters.country.trim() !== '') {
            filteredData = filteredData.filter((d: T): boolean =>
                d.Country.toLowerCase().includes(filters.country.toLowerCase())
            );
        }
        return Array.from(new Set(filteredData.map((d: T): string => d.Company_Name))).sort();
    }, [data, filters.region, filters.country]);

    // Calculate filtered data with text matching support
    const filteredData: T[] = React.useMemo((): T[] => {
        let result = data;

        // Region filter (exact match - empty means all regions)
        if (filters.region && filters.region.trim() !== '') {
            result = result.filter((d: T): boolean => d.Region === filters.region);
        }

        // Country filter (text contains - case insensitive)
        if (filters.country && filters.country.trim() !== '') {
            result = result.filter((d: T): boolean =>
                d.Country.toLowerCase().includes(filters.country.toLowerCase())
            );
        }

        // Company filter (text contains - case insensitive)
        if (filters.company && filters.company.trim() !== '') {
            result = result.filter((d: T): boolean =>
                d.Company_Name.toLowerCase().includes(filters.company.toLowerCase())
            );
        }

        return result;
    }, [data, filters]);

    // Notify parent component when filtered data changes
    React.useEffect((): void => {
        onFilteredDataChange(filteredData);
    }, [filteredData, onFilteredDataChange]);

    // Calculate unique plants count for display
    const uniquePlantsCount: number = React.useMemo((): number => {
        return new Set(filteredData.map(d => d.Plant_ID)).size;
    }, [filteredData]);

    // Handle filter changes
    const handleRegionChange = (event: React.SyntheticEvent, value: string | null): void => {
        const newRegion = value ?? '';
        const newFilters: FilterData = {
            region: newRegion,
            country: '', // Reset country text filter
            company: filters.company  // Keep company filter
        };
        onFiltersChange(newFilters);
    };

    const handleCountryChange = (event: React.SyntheticEvent, value: string | null): void => {
        const newCountry = value ?? '';
        const newFilters: FilterData = {
            ...filters,
            country: newCountry
            // Keep company filter unchanged
        };
        onFiltersChange(newFilters);
    };

    const handleCompanyChange = (event: React.SyntheticEvent, value: string | null): void => {
        const newFilters: FilterData = {
            ...filters,
            company: value ?? ''
        };
        onFiltersChange(newFilters);
    };

    // Dynamic helper text that updates based on current filters
    const regionHelperText = React.useMemo((): string => {
        return `${uniqueRegions.length} regions available`;
    }, [uniqueRegions.length]);

    const countryHelperText = React.useMemo((): string => {
        if (!filters.region || filters.region.trim() === '') {
            return `${availableCountries.length} countries available`;
        } else {
            return `${availableCountries.length} countries in ${filters.region}`;
        }
    }, [availableCountries.length, filters.region]);

    const companyHelperText = React.useMemo((): string => {
        const parts: string[] = [];

        if (filters.region && filters.region.trim() !== '') {
            parts.push(filters.region);
        }

        if (filters.country && filters.country.trim() !== '') {
            const matchingCountries = availableCountries.filter((country: string): boolean =>
                country.toLowerCase().includes(filters.country.toLowerCase())
            );
            if (matchingCountries.length === 1) {
                parts.push(matchingCountries[0]);
            } else if (matchingCountries.length > 1) {
                parts.push(`${matchingCountries.length} countries`);
            }
        }

        const locationText = parts.length > 0 ? ` in ${parts.join(' > ')}` : '';
        return `${availableCompanies.length} customers ${locationText}`;
    }, [availableCompanies.length, filters.region, filters.country, availableCountries]);

    // Helper function to get filter summary text
    const getFilterSummary = (): string => {
        const parts: string[] = [];

        if (filters.region && filters.region.trim() !== '') {
            parts.push(`in ${filters.region}`);
        }

        if (filters.country && filters.country.trim() !== '') {
            const matchingCountries = availableCountries.filter((country: string): boolean =>
                country.toLowerCase().includes(filters.country.toLowerCase())
            );
            if (matchingCountries.length > 0) {
                if (matchingCountries.length === 1) {
                    parts.push(`in ${matchingCountries[0]}`);
                } else {
                    parts.push(`in ${matchingCountries.length} countries matching "${filters.country}"`);
                }
            }
        }

        if (filters.company && filters.company.trim() !== '') {
            const matchingCompanies = availableCompanies.filter((company: string): boolean =>
                company.toLowerCase().includes(filters.company.toLowerCase())
            );
            if (matchingCompanies.length > 0) {
                if (matchingCompanies.length === 1) {
                    parts.push(`- ${matchingCompanies[0]}`);
                } else {
                    parts.push(`- ${matchingCompanies.length} companies matching "${filters.company}"`);
                }
            }
        }

        return parts.length > 0 ? ` ${parts.join(' ')}` : '';
    };

    return (
        <Stack direction="row" display="flex" spacing={spacing} sx={{ mb: 3, paddingY: 2, ...sx }}>
            {/* Region - Use Autocomplete with freeSolo */}
            <Autocomplete
                freeSolo
                options={uniqueRegions}
                value={filters.region}
                onChange={handleRegionChange}
                onInputChange={(event, newInputValue) => {
                    const newFilters: FilterData = {
                        ...filters,
                        region: newInputValue,
                        country: '', // Reset country when region changes
                        company: filters.company  // Keep company filter
                    };
                    onFiltersChange(newFilters);
                }}
                sx={{ minWidth: minWidths.region }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Region Filter"
                        size="small"
                        placeholder="Type or select region..."
                        helperText={regionHelperText}
                    />
                )}
                filterOptions={(options, { inputValue }) => {
                    // Custom filter to match partial text
                    return options.filter((option) =>
                        option.toLowerCase().includes(inputValue.toLowerCase())
                    );
                }}
            />

            {/* Country - Use Autocomplete with freeSolo */}
            <Autocomplete
                freeSolo
                options={availableCountries}
                value={filters.country}
                onChange={handleCountryChange}
                onInputChange={(event, newInputValue) => {
                    const newFilters: FilterData = {
                        ...filters,
                        country: newInputValue
                        // Keep company filter unchanged
                    };
                    onFiltersChange(newFilters);
                }}
                sx={{ minWidth: minWidths.country }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Country Filter"
                        size="small"
                        placeholder="Type or select country..."
                        helperText={countryHelperText}
                    />
                )}
                filterOptions={(options, { inputValue }) => {
                    // Custom filter to match partial text
                    return options.filter((option) =>
                        option.toLowerCase().includes(inputValue.toLowerCase())
                    );
                }}
            />

            {/* Company - Use Autocomplete with freeSolo */}
            <Autocomplete
                freeSolo
                options={availableCompanies}
                value={filters.company}
                onChange={handleCompanyChange}
                onInputChange={(event, newInputValue) => {
                    const newFilters: FilterData = {
                        ...filters,
                        company: newInputValue
                    };
                    onFiltersChange(newFilters);
                }}
                sx={{ minWidth: minWidths.company }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Company Filter"
                        size="small"
                        placeholder="Type or select company..."
                        helperText={companyHelperText}
                    />
                )}
                filterOptions={(options, { inputValue }) => {
                    // Custom filter to match partial text
                    return options.filter((option) =>
                        option.toLowerCase().includes(inputValue.toLowerCase())
                    );
                }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize:'75%' }}>
                Showing {uniquePlantsCount} records<br/>{getFilterSummary()}
            </Typography>
        </Stack>
    );
};

export default PlantsFilter;